// src/pages/CreateGroupPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Globe, Lock, EyeOff,
  Users, ChevronRight, Check,
} from 'lucide-react';
import { groupsAPI } from '../api/groupsApi';
import UserSearchInput from '../components/Groups/UserSearchInput';
import './CreateGroupPage.css';

const STEPS = ['Details', 'Settings', 'Invite'];

const VISIBILITY_OPTIONS = [
  {
    value: 'public',
    icon: <Globe size={18} />,
    label: 'Public',
    desc: 'Anyone can discover and join directly',
  },
  {
    value: 'private',
    icon: <Lock size={18} />,
    label: 'Private',
    desc: 'Visible to all, but joining requires invitation',
  },
  {
    value: 'secret',
    icon: <EyeOff size={18} />,
    label: 'Secret',
    desc: 'Only members can see this group',
  },
];

// Uses role vocabulary (viewer/editor) — matches GroupMembership.ROLE_CHOICES
const DEFAULT_ROLE_OPTIONS = [
  {
    value: 'viewer',
    label: 'Viewer',
    desc: 'New members can only view shared resources',
  },
  {
    value: 'editor',
    label: 'Editor',
    desc: 'New members can view and edit shared resources',
  },
];

const INVITE_ROLES = ['viewer', 'editor', 'admin'];

const CreateGroupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1 – Details
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Step 2 – Settings
  const [visibility, setVisibility] = useState('private');
  const [defaultMemberRole, setDefaultMemberRole] = useState('viewer');
  const [allowMemberInvite, setAllowMemberInvite] = useState(false);
  const [requireApproval, setRequireApproval] = useState(true);

  // Step 3 – Invite
  const [inviteUsers, setInviteUsers] = useState([]);
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteMessage, setInviteMessage] = useState('');

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);
  };

  // When visibility changes away from public, reset default role
  const handleVisibilityChange = (value) => {
    setVisibility(value);
    if (value !== 'public') {
      setDefaultMemberRole('viewer');
    }
  };

  const validateStep = () => {
    if (step === 0 && !name.trim()) {
      setError('Group name is required.');
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError('');
    try {
      const initial_members = inviteUsers.map((u) => ({
        user_id: u.id,
        role: inviteRole,
        message: inviteMessage,
      }));

      await groupsAPI.createGroup({
        name,
        description,
        avatar: avatarFile,
        visibility,
        // Only send default_member_role when public; backend also enforces this
        default_member_role: visibility === 'public' ? defaultMemberRole : 'viewer',
        allow_member_invite: allowMemberInvite,
        require_invite_approval: requireApproval,
        initial_members,
      });

      navigate('/groups/my');
    } catch (err) {
      setError(
        err.response?.data?.name?.[0] ||
        err.response?.data?.detail ||
        'Failed to create group.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-group-page">
      {/* Header */}
      <div className="create-group-page__header">
        <button className="btn btn--ghost btn--sm" onClick={() => navigate('/groups')}>
          <ArrowLeft size={15} /> Back
        </button>
        <h1 className="create-group-page__title">Create Research Group</h1>
      </div>

      {/* Stepper */}
      <div className="stepper">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div
              className={`stepper__step ${i <= step ? 'stepper__step--active' : ''} ${i < step ? 'stepper__step--done' : ''}`}
            >
              <div className="stepper__circle">
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className="stepper__label">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`stepper__line ${i < step ? 'stepper__line--done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Card */}
      <div className="create-group-page__card">

        {/* ── Step 0: Details ── */}
        {step === 0 && (
          <div className="create-group-page__step">
            <h2 className="create-group-page__step-title">Group Details</h2>

            <div className="form-group form-group--center">
              <div className="avatar-upload">
                <div className="avatar-upload__preview">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Group avatar" />
                  ) : (
                    <div className="avatar-upload__placeholder">
                      {name ? name.charAt(0).toUpperCase() : <Users size={28} />}
                    </div>
                  )}
                </div>
                <label className="avatar-upload__btn btn btn--ghost btn--sm">
                  <Upload size={14} /> Upload logo
                  <input
                    type="file"
                    accept="image/*"
                    className="avatar-upload__input"
                    onChange={handleAvatarChange}
                  />
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Group Name *</label>
              <input
                className="form-input"
                type="text"
                placeholder="e.g. Spectroscopy Lab 2024"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="What is this group about?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* ── Step 1: Settings ── */}
        {step === 1 && (
          <div className="create-group-page__step">
            <h2 className="create-group-page__step-title">Group Settings</h2>

            {/* Visibility */}
            <div className="form-group">
              <label className="form-label">Visibility</label>
              <div className="option-cards">
                {VISIBILITY_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className={`option-card ${visibility === opt.value ? 'option-card--selected' : ''}`}
                    onClick={() => handleVisibilityChange(opt.value)}
                    role="radio"
                    aria-checked={visibility === opt.value}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleVisibilityChange(opt.value)}
                  >
                    <div className="option-card__icon">{opt.icon}</div>
                    <div className="option-card__body">
                      <span className="option-card__label">{opt.label}</span>
                      <span className="option-card__desc">{opt.desc}</span>
                    </div>
                    {visibility === opt.value && (
                      <Check size={16} className="option-card__check" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Default member role — only shown for public groups */}
            {visibility === 'public' && (
              <div className="form-group">
                <label className="form-label">
                  Default Role for New Members
                  <span className="form-label__hint">
                    Role assigned when someone joins without an invitation
                  </span>
                </label>
                <div className="option-cards option-cards--row">
                  {DEFAULT_ROLE_OPTIONS.map((opt) => (
                    <div
                      key={opt.value}
                      className={`option-card ${defaultMemberRole === opt.value ? 'option-card--selected' : ''}`}
                      onClick={() => setDefaultMemberRole(opt.value)}
                      role="radio"
                      aria-checked={defaultMemberRole === opt.value}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setDefaultMemberRole(opt.value)}
                    >
                      <div className="option-card__body">
                        <span className="option-card__label">{opt.label}</span>
                        <span className="option-card__desc">{opt.desc}</span>
                      </div>
                      {defaultMemberRole === opt.value && (
                        <Check size={16} className="option-card__check" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Toggles */}
            <div className="form-group">
              <label className="form-label">Additional Settings</label>
              <div className="toggle-list">
                <label className="toggle-item">
                  <div className="toggle-item__info">
                    <span className="toggle-item__label">Allow editors to invite</span>
                    <span className="toggle-item__desc">
                      Editors (not just admins) can invite new members
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    className="toggle-input"
                    checked={allowMemberInvite}
                    onChange={(e) => setAllowMemberInvite(e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>

                {/* Approval toggle only makes sense for public groups */}
                {visibility === 'public' && (
                  <label className="toggle-item">
                    <div className="toggle-item__info">
                      <span className="toggle-item__label">
                        Require approval for join requests
                      </span>
                      <span className="toggle-item__desc">
                        Admins must approve before new members gain access
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="toggle-input"
                      checked={requireApproval}
                      onChange={(e) => setRequireApproval(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Invite ── */}
        {step === 2 && (
          <div className="create-group-page__step">
            <h2 className="create-group-page__step-title">Invite Members</h2>
            <p className="create-group-page__step-desc">
              Optionally invite colleagues now. You can always invite more later.
            </p>

            <div className="form-group">
              <label className="form-label">Add people</label>
              <UserSearchInput
                selectedUsers={inviteUsers}
                onAdd={(u) =>
                  setInviteUsers((prev) =>
                    prev.find((x) => x.id === u.id) ? prev : [...prev, u]
                  )
                }
                onRemove={(u) =>
                  setInviteUsers((prev) => prev.filter((x) => x.id !== u.id))
                }
              />
            </div>

            {inviteUsers.length > 0 && (
              <>
                <div className="form-group">
                  <label className="form-label">Invite as</label>
                  {visibility === 'public' && (
                    <p className="form-label__hint">
                      Minimum role is <strong>{defaultMemberRole}</strong> (group default)
                    </p>
                  )}
                  <div className="role-selector">
                    {INVITE_ROLES.map((r) => {
                      // For public groups, disable roles below the default
                      const isDisabled =
                        visibility === 'public' &&
                        ['viewer', 'editor'].indexOf(r) <
                          ['viewer', 'editor'].indexOf(defaultMemberRole);
                      return (
                        <button
                          key={r}
                          type="button"
                          className={`role-selector__btn ${inviteRole === r ? 'role-selector__btn--active' : ''} ${isDisabled ? 'role-selector__btn--disabled' : ''}`}
                          onClick={() => !isDisabled && setInviteRole(r)}
                          disabled={isDisabled}
                          title={
                            isDisabled
                              ? `Cannot invite below the group default (${defaultMemberRole})`
                              : undefined
                          }
                        >
                          {r}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Message (optional)</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    placeholder="Add a personal message to the invitation…"
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    maxLength={500}
                  />
                </div>
              </>
            )}
          </div>
        )}

        {error && <p className="form-error">{error}</p>}

        {/* Navigation */}
        <div className="create-group-page__nav">
          {step > 0 && (
            <button className="btn btn--ghost" onClick={handleBack} disabled={loading}>
              Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < STEPS.length - 1 ? (
            <button className="btn btn--primary" onClick={handleNext}>
              Next <ChevronRight size={15} />
            </button>
          ) : (
            <button
              className="btn btn--primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Creating…' : 'Create Group'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateGroupPage;
